/**
 * Campaign setup draft — instant in the browser, durable on the server.
 *
 * The setup form is long: world, opening, tone, house rules, a backstory per
 * player. It used to live in localStorage under one fixed key, which meant a GM
 * had exactly one draft, in exactly one browser, and it was deleted the instant
 * the campaign was created. Everything they had written was unreachable a
 * second later.
 *
 * Both layers stay, doing different jobs. localStorage is what makes typing
 * feel instant and what survives a refresh with no round trip — it is written
 * first and read on mount, so the form is never waiting on the network. The
 * server copy is the one that outlives the tab, the browser and the campaign.
 *
 * Creating a campaign no longer clears anything. The draft is marked as having
 * made that campaign and kept, and the form moves to a fresh one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createCampaignDraft,
  saveCampaignDraft,
  type CampaignDraftRow,
} from '@/api/client';

import { type CampaignDraft, emptyDraft } from './premise';

const STORAGE_KEY = 'throughline.campaignSetupDraft.v1';
/** Which server row the local draft belongs to, so a refresh keeps editing it
 *  rather than stranding it and starting another. */
const ID_KEY = 'throughline.campaignSetupDraft.serverId.v1';
const SAVE_DEBOUNCE_MS = 600;
/** The server is written less often than localStorage: it is a round trip, and
 *  nothing is lost by it lagging a couple of seconds behind the local copy. */
const SERVER_DEBOUNCE_MS = 2500;

export type SaveState = 'idle' | 'saving' | 'saved' | 'local-only';

export function useCampaignDraft() {
  const [draft, setDraft] = useState<CampaignDraft>(() => load() ?? emptyDraft());
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [serverId, setServerId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ID_KEY);
    } catch {
      return null;
    }
  });
  const timer = useRef<number | undefined>(undefined);
  const serverTimer = useRef<number | undefined>(undefined);
  // Skip the save that fires from simply mounting with a restored draft —
  // otherwise opening the form rewrites a row nobody edited.
  const touched = useRef(false);

  useEffect(() => {
    if (!touched.current) return;
    setSaveState('saving');
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...draft, savedAt: new Date().toISOString() }),
        );
        setSaveState('saved');
      } catch {
        // Private browsing, or quota. Not worth interrupting setup over —
        // and the server copy below is unaffected.
        setSaveState('local-only');
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current);
    };
  }, [draft]);

  // The durable copy.
  useEffect(() => {
    if (!touched.current) return;
    if (serverTimer.current !== undefined) window.clearTimeout(serverTimer.current);
    serverTimer.current = window.setTimeout(() => {
      const name = draft.name?.trim() || 'Untitled draft';
      const write = serverId
        ? saveCampaignDraft(serverId, name, draft)
        : createCampaignDraft(name, draft);
      void write
        .then((row) => rememberServerId(row.id, setServerId))
        .catch(() => {
          // Offline, or signed out. The local copy already has it, and the
          // next keystroke retries — so this must not shout at a GM who is
          // mid-sentence.
          setSaveState((s) => (s === 'saved' ? 'local-only' : s));
        });
    }, SERVER_DEBOUNCE_MS);

    return () => {
      if (serverTimer.current !== undefined) window.clearTimeout(serverTimer.current);
    };
  }, [draft, serverId]);

  const update = useCallback(
    <K extends keyof CampaignDraft>(key: K, value: CampaignDraft[K]) => {
      touched.current = true;
      setDraft((d) => ({ ...d, [key]: value }));
    },
    [],
  );

  /** Load a stored draft into the form — picked from the list, or duplicated. */
  const adopt = useCallback((row: CampaignDraftRow) => {
    touched.current = false;
    setDraft({ ...emptyDraft(), ...(row.draft_json as Partial<CampaignDraft>) });
    rememberServerId(row.id, setServerId);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...row.draft_json, savedAt: new Date().toISOString() }),
      );
    } catch {
      /* local cache is optional */
    }
    setSaveState('saved');
  }, []);

  /**
   * The campaign was created. Keep the draft; start a clean one.
   *
   * This used to delete it. A GM who then noticed a wrong party level had no
   * way to see what they had typed.
   */
  const startFresh = useCallback(() => {
    touched.current = false;
    setDraft(emptyDraft());
    setSaveState('idle');
    setServerId(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ID_KEY);
    } catch {
      /* nothing to clean up */
    }
  }, []);

  return { draft, setDraft, update, adopt, startFresh, saveState, serverId };
}

function rememberServerId(id: string, set: (v: string) => void): void {
  set(id);
  try {
    localStorage.setItem(ID_KEY, id);
  } catch {
    /* the draft still saves server-side; only the link is lost on reload */
  }
}

function load(): CampaignDraft | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CampaignDraft>;
    // Merge over a fresh draft so a schema change doesn't leave holes.
    return {
      ...emptyDraft(),
      ...parsed,
      players: parsed.players?.length ? parsed.players : emptyDraft().players,
    };
  } catch {
    return null;
  }
}
